import { z } from "zod/v4";

export const httpMethodSchema = z.enum([
	"GET",
	"POST",
	"PUT",
	"PATCH",
	"DELETE",
	"HEAD",
	"OPTIONS",
]);
export type HttpMethod = z.infer<typeof httpMethodSchema>;

export const kvSchema = z.object({
	enabled: z.boolean().optional().catch(undefined),
	key: z.string().default("").catch(""),
	value: z.string().default("").catch(""),
});
export type Kv = z.infer<typeof kvSchema>;

export const bodyModeSchema = z.enum([
	"none",
	"text",
	"json",
	"raw",
	"form",
	"form-urlencoded",
	"xml",
]);
export type BodyMode = z.infer<typeof bodyModeSchema>;

const authNoneSchema = z.object({
	type: z.literal("none"),
});

const authBasicSchema = z.object({
	type: z.literal("basic"),
	username: z.string().optional(),
	password: z.string().optional(),
});

const authBearerSchema = z.object({
	type: z.literal("bearer"),
	token: z.string().optional(),
});

const authDigestSchema = z.object({
	type: z.literal("digest"),
	username: z.string().optional(),
	password: z.string().optional(),
});

const authOAuth2Schema = z.object({
	type: z.literal("oauth2"),
	accessToken: z.string().optional(),
	refreshToken: z.string().optional(),
	tokenUrl: z.string().optional(),
	clientId: z.string().optional(),
	clientSecret: z.string().optional(),
	scope: z.string().optional(),
});

const authKubernetesSchema = z.object({
	type: z.literal("kubernetes"),
	context: z.string().optional(),
});

export const authSchema = z.discriminatedUnion("type", [
	authNoneSchema,
	authBasicSchema,
	authBearerSchema,
	authDigestSchema,
	authOAuth2Schema,
	authKubernetesSchema,
]);
export type Auth = z.infer<typeof authSchema>;

export const requestBodySchema = z.object({
	mode: bodyModeSchema.default("none").catch("none"),
	text: z.string().default("").catch(""),
	form: z.array(kvSchema).default([]).catch([]),
});
export type RequestBody = z.infer<typeof requestBodySchema>;

// =============================================================================
// HTTP request spec
// =============================================================================

export const httpRequestSpecSchema = z.object({
	type: z.literal("http").default("http"),
	method: httpMethodSchema.default("GET").catch("GET"),
	url: z.string().default("").catch(""),
	params: z.array(kvSchema).default([]).catch([]),
	headers: z.array(kvSchema).default([]).catch([]),
	auth: authSchema.default({ type: "none" }).catch({ type: "none" }),
	body: requestBodySchema
		.default({ mode: "none", text: "", form: [] })
		.catch({ mode: "none", text: "", form: [] }),
	/**
	 * Named variables for `{{vars.X}}` substitution in the URL, headers, body, and auth fields.
	 * These are caller-injectable values passed via `query({ vars: {...} })` or
	 * `mutateAsync({ vars: {...} })`. Unlike `params`, these do not become URL query-string
	 * parameters — they are solely for template substitution.
	 */
	vars: z.array(kvSchema).default([]).catch([]),
	timeoutMs: z
		.number()
		.int()
		.positive()
		.max(5 * 60 * 1000)
		.default(30_000)
		.catch(30_000),
	followRedirects: z.boolean().default(true).catch(true),
	validateSSL: z.boolean().default(true).catch(true),
});
export type HttpRequestSpec = z.infer<typeof httpRequestSpecSchema>;

// =============================================================================
// Postgres request spec
// =============================================================================

export const postgresRequestSpecSchema = z.object({
	type: z.literal("postgres"),
	/** Connection string, typically an env-var reference: {{env.DATABASE_URL}} */
	connectionString: z.string().default("{{env.DATABASE_URL}}").catch("{{env.DATABASE_URL}}"),
	/** SQL statement. */
	sql: z.string().default("").catch(""),
	/**
	 * Named variables for `{{vars.X}}` (or `{{params.X}}`) text substitution in the SQL and
	 * connection string. These provide default values that callers can override via
	 * `query({ vars: {...} })` or `mutateAsync({ vars: {...} })`.
	 * Note: these are substituted as text before execution, not as parameterized query arguments.
	 */
	params: z.array(kvSchema).default([]).catch([]),
	timeoutMs: z
		.number()
		.int()
		.positive()
		.max(5 * 60 * 1000)
		.default(30_000)
		.catch(30_000),
});
export type PostgresRequestSpec = z.infer<typeof postgresRequestSpecSchema>;

// =============================================================================
// SQLite request spec
// =============================================================================

export const sqliteRequestSpecSchema = z.object({
	type: z.literal("sqlite"),
	/** Absolute path to the .sqlite, .db, or .csv file (from document attachments). */
	filePath: z.string().default("").catch(""),
	/** SQL statement to execute against the file. */
	sql: z.string().default("").catch(""),
	/**
	 * Named variables for `{{vars.X}}` (or `{{params.X}}`) text substitution in the SQL.
	 * Note: these are substituted as text before execution, not as parameterized query arguments.
	 */
	params: z.array(kvSchema).default([]).catch([]),
	timeoutMs: z
		.number()
		.int()
		.positive()
		.max(5 * 60 * 1000)
		.default(30_000)
		.catch(30_000),
});
export type SqliteRequestSpec = z.infer<typeof sqliteRequestSpecSchema>;

// =============================================================================
// JavaScript request spec
// =============================================================================

export const javascriptRequestSpecSchema = z.object({
	type: z.literal("javascript"),
	/** JavaScript source code. */
	code: z.string().default("").catch(""),
	/**
	 * Named parameters for {{params.X}} text substitution in the code.
	 * Note: these are substituted as text before execution, not as runtime arguments.
	 */
	params: z.array(kvSchema).default([]).catch([]),
	timeoutMs: z
		.number()
		.int()
		.positive()
		.max(5 * 60 * 1000)
		.default(30_000)
		.catch(30_000),
});
export type JavaScriptRequestSpec = z.infer<typeof javascriptRequestSpecSchema>;

/** @deprecated Use javascriptRequestSpecSchema directly. */
export const awsRequestSpecSchema = javascriptRequestSpecSchema;
/** @deprecated Use JavaScriptRequestSpec directly. */
export type AwsRequestSpec = JavaScriptRequestSpec;

// =============================================================================
// gRPC request spec
// =============================================================================

export const grpcRequestSpecSchema = z.object({
	type: z.literal("grpc"),
	/** gRPC server address, e.g. "localhost:50051" */
	url: z.string().default("localhost:50051").catch("localhost:50051"),
	/** Paths to .proto files for service discovery */
	protoFiles: z.array(z.string()).default([]).catch([]),
	/** Import paths for proto file resolution */
	importPaths: z.array(z.string()).default([]).catch([]),
	/** Whether to use server reflection instead of proto files */
	useReflection: z.boolean().default(false).catch(false),
	/** Fully qualified service name, e.g. "package.ServiceName" */
	service: z.string().default("").catch(""),
	/** Method name, e.g. "MethodName" */
	method: z.string().default("").catch(""),
	/** JSON request message body */
	message: z.string().default("{}").catch("{}"),
	/** gRPC metadata (headers) */
	metadata: z.array(kvSchema).default([]).catch([]),
	/** Authentication configuration */
	auth: authSchema.default({ type: "none" }).catch({ type: "none" }),
	/** Path to CA certificate for TLS */
	tlsCaCertPath: z.string().optional(),
	/** Path to client certificate for mutual TLS */
	tlsClientCertPath: z.string().optional(),
	/** Path to client key for mutual TLS */
	tlsClientKeyPath: z.string().optional(),
	/** Skip TLS certificate verification */
	tlsSkipVerify: z.boolean().default(false).catch(false),
	/** Named variables for {{vars.X}} substitution */
	vars: z.array(kvSchema).default([]).catch([]),
	timeoutMs: z
		.number()
		.int()
		.positive()
		.max(5 * 60 * 1000)
		.default(30_000)
		.catch(30_000),
});
export type GrpcRequestSpec = z.infer<typeof grpcRequestSpecSchema>;

// =============================================================================
// Discriminated request spec union
// =============================================================================

export const requestSpecSchema = z
	.preprocess(
		(val) => {
			if (typeof val !== "object" || val === null) return val;
			if (!("type" in val)) return { ...val, type: "http" };
			// Migrate legacy "aws" type to "javascript"
			if ((val as Record<string, unknown>).type === "aws")
				return { ...val, type: "javascript" };
			return val;
		},
		z.discriminatedUnion("type", [
			httpRequestSpecSchema,
			postgresRequestSpecSchema,
			sqliteRequestSpecSchema,
			javascriptRequestSpecSchema,
			grpcRequestSpecSchema,
		])
	)
	.catch({
		type: "http",
		method: "GET",
		url: "",
		params: [],
		headers: [],
		auth: { type: "none" },
		body: { mode: "none", text: "", form: [] },
		vars: [],
		timeoutMs: 30_000,
		followRedirects: true,
		validateSSL: true,
	});
export type RequestSpec = z.infer<typeof requestSpecSchema>;

// =============================================================================
// Legacy alias — kept for back-compat; resolves to httpRequestSpecSchema
// =============================================================================

/**
 * @deprecated Use httpRequestSpecSchema directly.
 * Alias preserved so existing consumers of `requestSchema` do not break.
 */
export const requestSchema = httpRequestSpecSchema;

// =============================================================================
// UI state
// =============================================================================

export const uiSchema = z.object({
	layout: z.enum(["horizontal", "vertical"]).default("horizontal").catch("horizontal"),
	showSchemaPane: z.boolean().default(true).catch(true),
	activeTab: z
		.enum([
			"body",
			"params",
			"headers",
			"auth",
			"info",
			"sql",
			"query",
			"vars",
			"message",
			"metadata",
			"proto",
		])
		.default("body")
		.catch("body"),
});
export type HttpRequestUI = z.infer<typeof uiSchema>;

// =============================================================================
// Request page document (outer wrapper, persisted to YAML)
// =============================================================================

export const HttpRequestPageSchema = z.object({
	schemaVersion: z.number().int().default(1),
	kind: z.literal("Page"),
	pageType: z.literal("request"),
	request: requestSpecSchema,
	ui: uiSchema.optional(),
});

export type HttpRequestPage = z.infer<typeof HttpRequestPageSchema>;

// =============================================================================
// Defaults
// =============================================================================

export function getDefaultHttpRequestPage(): HttpRequestPage {
	return {
		schemaVersion: 1,
		kind: "Page",
		pageType: "request",
		request: {
			type: "http",
			method: "GET",
			// This may go away, but we should still use it
			// to support NOAA.
			// As always, from Church of Powder Day Saints,
			// pray for snow
			url: "https://api.weather.gov/gridpoints/SEW/137,30/forecast",
			params: [],
			headers: [],
			auth: { type: "none" },
			body: { mode: "none", text: "", form: [] },
			vars: [],
			timeoutMs: 30_000,
			followRedirects: true,
			validateSSL: true,
		},
		ui: { layout: "horizontal", showSchemaPane: true, activeTab: "body" },
	};
}

export function getDefaultJavaScriptRequestPage(): HttpRequestPage {
	return {
		schemaVersion: 1,
		kind: "Page",
		pageType: "request",
		request: {
			type: "javascript",
			code: `const { STSClient, GetCallerIdentityCommand } = await import("https://cdn.skypack.dev/@aws-sdk/client-sts@3.716.0");\nconst region = env._aws.region;\nconst client = new STSClient({\n  region,\n  endpoint: \`https://sts.\${region}.amazonaws.com\`,\n  credentials: env._aws.credentials,\n});\nconst identity = await client.send(new GetCallerIdentityCommand({}));\nreturn identity;`,
			params: [],
			timeoutMs: 30_000,
		},
		ui: { layout: "horizontal", showSchemaPane: false, activeTab: "body" },
	};
}

/** @deprecated Use getDefaultJavaScriptRequestPage directly. */
export const getDefaultAwsRequestPage = getDefaultJavaScriptRequestPage;

export function getDefaultGrpcRequestPage(): HttpRequestPage {
	return {
		schemaVersion: 1,
		kind: "Page",
		pageType: "request",
		request: {
			type: "grpc",
			url: "localhost:50051",
			protoFiles: [],
			importPaths: [],
			useReflection: true,
			service: "",
			method: "",
			message: "{}",
			metadata: [],
			auth: { type: "none" },
			tlsSkipVerify: false,
			vars: [],
			timeoutMs: 30_000,
		},
		ui: { layout: "horizontal", showSchemaPane: false, activeTab: "message" },
	};
}

export function getDefaultPostgresRequestPage(): HttpRequestPage {
	return {
		schemaVersion: 1,
		kind: "Page",
		pageType: "request",
		request: {
			type: "postgres",
			connectionString: "{{env.DATABASE_URL}}",
			sql: "SELECT 1",
			params: [],
			timeoutMs: 30_000,
		},
		ui: { layout: "horizontal", showSchemaPane: false, activeTab: "query" },
	};
}

/** Default SQL for CSV files (loaded into an in-memory table called `data`). */
export const DEFAULT_CSV_SQL = "SELECT *\nFROM data\nLIMIT 100\n";

/** Default SQL for .sqlite/.db files (discover available tables). */
export const DEFAULT_SQLITE_DB_SQL = "SELECT name\nFROM sqlite_master\nWHERE type='table'\n";

export function getDefaultSqliteRequestPage(): HttpRequestPage {
	return {
		schemaVersion: 1,
		kind: "Page",
		pageType: "request",
		request: {
			type: "sqlite",
			filePath: "",
			sql: DEFAULT_CSV_SQL,
			params: [],
			timeoutMs: 30_000,
		},
		ui: { layout: "horizontal", showSchemaPane: false, activeTab: "query" },
	};
}
